/**
 * `cofactor alert` — notify-only rules.
 *
 * An alert is an automation rule with `actionType:'notify'` (a no-op action
 * whose only job is to let `alertConfig.steps` fire Slack/email). By default an
 * alert fires after a test suite completes; it can also be time-based. Alerts
 * live in the same `automation_rules` table as `schedule` rules but are kept
 * separate in the CLI — this group only ever creates/lists `notify` rules.
 *
 * @module alert
 */

import process from 'node:process';
import { getArgValue, hasFlag, resolveConfig } from './auth.js';
import {
  buildScheduleTriggerConfig,
  buildSteps,
  createRule,
  fail,
  getRuleOrExit,
  handleDelete,
  handleGet,
  handleList,
  handlePauseResume,
  handleRun,
  handleRuns,
  hasTimingFlags,
  mergeSteps,
  parseFilters,
  updateRule,
  type AlertStep,
  type TriggerConfig,
} from './automation-shared.js';
import { formatSlackChannel, loadSlackChannels } from './slack-channels.js';

const ACTION_TYPE = 'notify';
const SYSTEM_EVENTS = ['test_suite.completed', 'release_qa.completed'];

function printAlertHelp(): void {
  console.log(
    [
      'Usage: cofactor alert <sub-command> [options]',
      '',
      'Sub-commands:',
      '  list [--enabled true|false] [--page N] [--json]',
      '  get <id> [--json]',
      '  create  <flags>                      Create a notify-only alert',
      '  edit <id> <flags>                    Edit an alert',
      '  pause <id>                           Pause (disable) an alert',
      '  resume <id>                          Resume (enable) an alert',
      '  delete <id>                          Delete an alert',
      '  run <id>                             Fire now',
      '  runs <id> [--status <s>] [--json]    List recent firings',
      '',
      'create/edit flags:',
      '  --name <s>                           Required on create',
      '  --description <s>',
      '  --disabled                           Create paused (create only)',
      '  Trigger (default: after a test suite completes):',
      `    --event <e>                        ${SYSTEM_EVENTS.join(' | ')} (default test_suite.completed)`,
      '    --schedule/--cron/--at/--every     Time-based instead of event-based',
      '    --timezone <IANA>',
      '  Channel (at least one):',
      '    --email <addr>                     Email recipient (repeatable/comma)',
      '    --slack <channel>                  Slack channel: ID (C…), #name, or name (repeatable/comma)',
      '  --when success,failure               When to fire (default failure)',
      '  --filter "field:op:value"            Only fire when condition holds (repeatable)',
      '',
      'Attach to a schedule (instead of a standalone alert):',
      '  --on-schedule <scheduleId>           Write the channels onto an existing scheduled',
      '                                       suite run so they fire only for that schedule.',
      '                                       (Standalone alerts on test_suite.completed do NOT',
      '                                       fire for scheduled runs.) Merges by default.',
      '  --replace-alerts                     Replace the schedule\'s alert steps instead of merging',
      '  --clear-alerts                       Remove all alert steps from the schedule',
      '',
      'Common: --env prod|dev|local, --json',
      '',
      'Examples:',
      '  # Slack a channel whenever a test suite finishes with failures',
      '  cofactor alert create --name "Suite failures" \\',
      '    --slack C0123ABCD --when failure \\',
      '    --filter "test_suite.status:equals:failed"',
      '',
      '  # Email a digest on every suite completion (pass or fail)',
      '  cofactor alert create --name "Suite digest" \\',
      '    --email qa@acme.com,lead@acme.com --when success,failure',
      '',
      '  # Attach a Slack alert to a scheduled suite run (fires for that schedule)',
      '  cofactor alert create --on-schedule <scheduleId> \\',
      '    --slack "#qa-alerts" --when success,failure',
      '',
      '  cofactor alert list',
      '  cofactor alert pause <id>              # and: resume <id>',
      '  cofactor alert run <id>                # fire now',
      '',
      'Note: alerts attached with --on-schedule live on the schedule rule, so they',
      'appear under `cofactor schedule suite get <id>`, not `cofactor alert list`.',
    ].join('\n')
  );
}

/**
 * Resolve the alert trigger. Timing flags ⇒ a schedule trigger; otherwise a
 * system completion event (default `test_suite.completed`).
 */
async function buildTrigger(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<{ triggerSource: string; triggerEvent: string; triggerConfig?: TriggerConfig }> {
  if (hasTimingFlags(argv)) {
    const triggerConfig = (await buildScheduleTriggerConfig(argv, apiUrl, token)) as TriggerConfig;
    return { triggerSource: 'schedule', triggerEvent: 'scheduled', triggerConfig };
  }
  const event = getArgValue(argv, '--event') ?? 'test_suite.completed';
  if (!SYSTEM_EVENTS.includes(event)) {
    fail(`--event must be one of: ${SYSTEM_EVENTS.join(', ')}`);
  }
  return { triggerSource: 'system', triggerEvent: event };
}

/** Summarize alert steps for confirmation output, resolving Slack names. */
function summarizeSteps(steps: AlertStep[], idToName?: Map<string, string>): string {
  if (steps.length === 0) return 'no channels';
  return steps
    .map((s) =>
      s.channel === 'email'
        ? `email→${(s.recipients ?? []).join(',')}`
        : `slack→${s.slackChannelId ? formatSlackChannel(s.slackChannelId, idToName) : '?'}`
    )
    .join(' ');
}

/**
 * Write alert steps directly onto an existing scheduled suite run's
 * `alertConfig`. This is how alerts get tied to a schedule: standalone
 * `test_suite.completed` alerts never fire for scheduled (automation-triggered)
 * runs, but a schedule rule's own steps do.
 */
async function attachToSchedule(
  scheduleId: string,
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const rule = await getRuleOrExit(apiUrl, token, scheduleId);
  if (rule.triggerSource !== 'schedule' || rule.actionType !== 'workflow.run-tests') {
    fail(
      `${scheduleId} is not a scheduled suite run ` +
        `(triggerSource=${rule.triggerSource}, actionType=${rule.actionType}). ` +
        'Use an id from `cofactor schedule suite list`.'
    );
  }

  const clear = hasFlag(argv, '--clear-alerts');
  const replace = hasFlag(argv, '--replace-alerts');
  const existing = rule.alertConfig?.steps ?? [];

  let steps: AlertStep[];
  if (clear) {
    steps = [];
  } else {
    const newSteps = await buildSteps(argv, apiUrl, token);
    if (newSteps === null) {
      fail(
        'At least one channel is required: --email <addr> or --slack <channel> ' +
          '(or use --clear-alerts).'
      );
    }
    steps = replace ? newSteps! : mergeSteps(existing, newSteps!);
  }

  await updateRule(apiUrl, token, scheduleId, { alertConfig: { steps } });

  const idToName = steps.some((s) => s.channel === 'slack')
    ? (await loadSlackChannels(apiUrl, token)).idToName
    : undefined;
  console.log(`  schedule "${rule.name}" alerts: ${summarizeSteps(steps, idToName)}`);
}

async function handleCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const onSchedule = getArgValue(argv, '--on-schedule');
  if (onSchedule !== undefined) {
    await attachToSchedule(onSchedule, argv, apiUrl, token);
    return;
  }

  const name = getArgValue(argv, '--name');
  if (!name) fail('--name is required.');

  const steps = await buildSteps(argv, apiUrl, token);
  if (steps === null) fail('At least one channel is required: --email <addr> or --slack <channel>.');

  const trigger = await buildTrigger(argv, apiUrl, token);

  const body: Record<string, unknown> = {
    name,
    enabled: !hasFlag(argv, '--disabled'),
    triggerSource: trigger.triggerSource,
    triggerEvent: trigger.triggerEvent,
    actionType: ACTION_TYPE,
    actionConfig: {},
    alertConfig: { steps },
  };
  if (trigger.triggerConfig) body.triggerConfig = trigger.triggerConfig;
  const description = getArgValue(argv, '--description');
  if (description !== undefined) body.description = description;
  const filters = parseFilters(argv);
  if (filters.length > 0) body.filterConfig = { conditions: filters };

  await createRule(apiUrl, token, body);
}

async function handleEdit(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv[0];
  if (!id) fail('Usage: alert edit <id> <flags>');
  const rest = argv.slice(1);

  const body: Record<string, unknown> = {};
  const name = getArgValue(rest, '--name');
  if (name !== undefined) body.name = name;
  const description = getArgValue(rest, '--description');
  if (description !== undefined) body.description = description;

  const steps = await buildSteps(rest, apiUrl, token);
  if (steps !== null) body.alertConfig = { steps };

  if (hasTimingFlags(rest) || getArgValue(rest, '--event') !== undefined) {
    const trigger = await buildTrigger(rest, apiUrl, token);
    body.triggerSource = trigger.triggerSource;
    body.triggerEvent = trigger.triggerEvent;
    if (trigger.triggerConfig) body.triggerConfig = trigger.triggerConfig;
  }

  const filters = parseFilters(rest);
  if (filters.length > 0) body.filterConfig = { conditions: filters };

  await updateRule(apiUrl, token, id, body);
}

export async function runAlert(argv: string[]): Promise<void> {
  const [sub, ...rest] = argv;
  if (!sub || sub === 'help' || hasFlag(argv, '--help', '-h')) {
    printAlertHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);
  const noun = 'alert';

  switch (sub) {
    case 'list':
      await handleList(apiUrl, token, { actionType: ACTION_TYPE }, rest, noun);
      break;
    case 'get':
      await handleGet(rest, apiUrl, token, noun);
      break;
    case 'create':
      await handleCreate(rest, apiUrl, token);
      break;
    case 'edit':
      await handleEdit(rest, apiUrl, token);
      break;
    case 'pause':
      await handlePauseResume(rest, apiUrl, token, false, noun);
      break;
    case 'resume':
      await handlePauseResume(rest, apiUrl, token, true, noun);
      break;
    case 'delete':
      await handleDelete(rest, apiUrl, token, noun);
      break;
    case 'run':
      await handleRun(rest, apiUrl, token, noun);
      break;
    case 'runs':
      await handleRuns(rest, apiUrl, token, noun);
      break;
    default:
      console.error(`Unknown sub-command: ${sub}`);
      printAlertHelp();
      process.exit(1);
  }
}
